import { analysisClient } from "@/shared/lib/supabase";
import type { Json } from "@/types/supabase.types";
import type { RecordOwner } from "./types";

const SYSTEM_USER_ID = "00000000-0000-0000-0000-000000000000";

type TenantUserRow = {
  auth_user_id: string;
  tenant_user_id: string;
};

type TenantUserSettingRow = {
  tenant_user_id: string;
  settings: Json | null;
};

function getInitials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);

  if (parts.length === 0) return "??";

  if (parts.length === 1) {
    const [firstPart = ""] = parts;
    return firstPart.slice(0, 2).toUpperCase();
  }

  const [firstPart = "", secondPart = ""] = parts;
  return `${firstPart[0] ?? ""}${secondPart[0] ?? ""}`.toUpperCase();
}

function nameFromSettings(settings: Json | null | undefined) {
  if (!settings || typeof settings !== "object" || Array.isArray(settings)) {
    return null;
  }

  const value = settings as Record<string, unknown>;
  const firstname =
    typeof value.firstname === "string" ? value.firstname.trim() : "";
  const lastname =
    typeof value.lastname === "string" ? value.lastname.trim() : "";
  const fullName = [firstname, lastname].filter(Boolean).join(" ").trim();

  return fullName || null;
}

function fallbackOwnerLabel(authUserId: string) {
  if (authUserId === SYSTEM_USER_ID) {
    return "Researchly";
  }

  return "Tenant resource";
}

export async function fetchRecordOwners(
  authUserIds: string[],
): Promise<Record<string, RecordOwner>> {
  const uniqueAuthUserIds = [...new Set(authUserIds.filter(Boolean))];
  if (uniqueAuthUserIds.length === 0) return {};

  const { data: tenantUsers, error: tenantUsersError } = await analysisClient
    .from("tenant_user")
    .select("auth_user_id, tenant_user_id")
    .in("auth_user_id", uniqueAuthUserIds);

  if (tenantUsersError) throw tenantUsersError;

  const tenantUserRows = (tenantUsers ?? []) as TenantUserRow[];
  const tenantUserIds = tenantUserRows.map((row) => row.tenant_user_id);

  let tenantUserSettings: TenantUserSettingRow[] = [];
  if (tenantUserIds.length > 0) {
    const { data: settings, error: settingsError } = await analysisClient
      .from("tenant_user_setting")
      .select("tenant_user_id, settings")
      .in("tenant_user_id", tenantUserIds);

    if (settingsError) throw settingsError;
    tenantUserSettings = settings ?? [];
  }

  const tenantUserByAuthUserId = new Map(
    tenantUserRows.map((row) => [row.auth_user_id, row]),
  );
  const settingsByTenantUserId = new Map(
    tenantUserSettings.map((row) => [row.tenant_user_id, row.settings]),
  );

  return uniqueAuthUserIds.reduce<Record<string, RecordOwner>>(
    (acc, authUserId) => {
      const tenantUser = tenantUserByAuthUserId.get(authUserId);
      const fullName =
        nameFromSettings(
          tenantUser
            ? settingsByTenantUserId.get(tenantUser.tenant_user_id)
            : null,
        ) ?? fallbackOwnerLabel(authUserId);

      acc[authUserId] = {
        name: fullName,
        initials: getInitials(fullName),
      };

      return acc;
    },
    {},
  );
}
